/**
 * SERVICIO DE CANDIDATOS CON SUPABASE 
 * 
 * Este módulo proporciona operaciones CRUD para candidatos usando los endpoints REST de Supabase.
 * Incluye funcionalidades para crear, leer, actualizar y eliminar candidatos.
 * 
 * * Características principales:
 * - Operaciones CRUD completas
 * - Autenticación con tokens JWT de Supabase
 * - Validación de datos
 * - Manejo de errores
 * - Filtros por diferentes campos
 * 
 * Mejoras implementadas:
 * - Validación de duplicados antes de insertar
 * - Manejo mejorado de errores en carga masiva
 * - Validación de campos requeridos
 * - Mejor transformación de datos desde Excel
 * - Feedback detallado de errores
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const candidateService = {
    /**
     * Obtiene el token de autenticación desde localStorage
     * @returns {string|null} Token de acceso o null si no existe
     */
    getAuthToken() {
        return localStorage.getItem('access_token');
    },

    /**
     * Obtiene los headers necesarios para las peticiones autenticadas
     * @returns {Object} Headers con autenticación
     */
    getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
        };
    },

    /**
     * Obtiene el ID de la empresa del usuario actual
     * @returns {string|null} ID de la empresa o null si no existe
     */
    getCurrentCompanyId() {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return null;

            const user = JSON.parse(userStr);
            return user.companyData?.id_empresa || null;
        } catch (error) {
            console.error('Error al obtener ID de empresa:', error);
            return null;
        }
    },

    /**
     * Transforma los datos del formulario al formato requerido por la API
     * @param {Object} formData - Datos del formulario
     * @returns {Object} Datos transformados para la API
     */
    transformFormDataToAPI(formData) {
        const companyId = this.getCurrentCompanyId();

        return {
            nombre_completo: formData.nombreCompleto || '',
            documento: parseInt(formData.numIdentificacion) || 0,
            email: formData.correo || '',
            fecha_nacimiento: formData.fechaNacimiento || null,
            edad: parseInt(formData.edad) || 0,
            genero: formData.genero?.toLowerCase() || '',
            direccion: formData.direccion || '',
            nivel_educativo: formData.nivelEducativo || '',
            experiencia_laboral: formData.experienciaLaboral || '',
            habilidades_destacadas: Array.isArray(formData.habilidadesDestacadas)
                ? formData.habilidadesDestacadas.join(', ')
                : formData.habilidadesDestacadas || '',
            motivacion_cargo: formData.motivacionCargo || '',
            cargo_aplicado: formData.cargoAplicado || '',
            ciudad: formData.ubicacionGeografica || '',
            pais: formData.pais || 'Colombia',
            id_empresa: companyId
        };
    },

    /**
     * Transforma los datos de la API al formato del formulario
     * @param {Object} apiData - Datos de la API
     * @returns {Object} Datos transformados para el formulario
     */
    transformAPIDataToForm(apiData) {
        return {
            nombreCompleto: apiData.nombre_completo || '',
            numIdentificacion: apiData.documento?.toString() || '',
            correo: apiData.email || '',
            fechaNacimiento: apiData.fecha_nacimiento || '',
            edad: apiData.edad?.toString() || '',
            genero: apiData.genero || '',
            direccion: apiData.direccion || '',
            nivelEducativo: apiData.nivel_educativo || '',
            experienciaLaboral: apiData.experiencia_laboral || '',
            habilidadesDestacadas: apiData.habilidades_destacadas?.split(', ') || [],
            motivacionCargo: apiData.motivacion_cargo || '',
            cargoAplicado: apiData.cargo_aplicado || '',
            ubicacionGeografica: apiData.ciudad || '',
            pais: apiData.pais || '',
            emociones: apiData.emociones || ''
        };
    },

    /**
     * NUEVO: Valida los datos antes de crear un candidato
     * validateCandidateData: verifica que los datos obligatorios estén presentes y tengan el formato correcto
     */
    validateCandidateData(data) {
        const errors = [];

        // Validar nombre completo
        if (!data.nombreCompleto || data.nombreCompleto.trim() === '') {
            errors.push('Nombre Completo es requerido');
        }

        // Validar documento
        if (!data.numIdentificacion || data.numIdentificacion.trim() === '') {
            errors.push('Número de Identificación es requerido');
        } else if (!/^\d+$/.test(data.numIdentificacion)) {
            errors.push('Número de Identificación debe contener solo dígitos');
        }

        // Validar correo
        if (!data.correo || data.correo.trim() === '') {
            errors.push('Correo es requerido');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(data.correo)) {
                errors.push('Correo electrónico no es válido');
            }
        }

        // Validar fecha de nacimiento (opcional pero si existe debe ser válida)
        if (data.fechaNacimiento) {
            const fecha = new Date(data.fechaNacimiento);
            if (isNaN(fecha.getTime())) {
                errors.push('Fecha de Nacimiento no es válida');
            }
        }

        return errors;
    },

    /**
     * NUEVO: Verifica si existe un candidato con el mismo documento o email
     * el metodo checkDuplicateCandidate realiza una consulta a la base de datos para verificar si ya existe un candidato con el mismo documento o email
     */
    async checkDuplicateCandidate(documento, email) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?or=(documento.eq.${documento},email.eq.${encodeURIComponent(email)})`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                return { exists: false, message: null };
            }

            const data = await response.json();
            
            if (data.length > 0) {
                const duplicate = data[0];
                if (duplicate.documento.toString() === documento.toString()) {
                    return { exists: true, message: `Ya existe un candidato con el documento ${documento}` };
                }
                if (duplicate.email === email) {
                    return { exists: true, message: `Ya existe un candidato con el email ${email}` };
                }
            }

            return { exists: false, message: null };
        } catch (error) {
            console.error('Error al verificar duplicados:', error);
            return { exists: false, message: null };
        }
    },

    /**
     * Obtiene todos los candidatos
     * @returns {Promise<Array>} Lista de candidatos
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     */
    async getAllCandidates() {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Obtiene un candidato específico por ID
     */
    async getCandidateById(candidateId) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?id_candidato=eq.${candidateId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidato' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error al obtener candidato:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /*
     * Crea un nuevo candidato
     */
    async createCandidate(candidateData) {
        try {
            const transformedData = this.transformFormDataToAPI(candidateData);

            if (!transformedData.nombre_completo || !transformedData.documento || !transformedData.email) {
                throw { message: 'Faltan campos obligatorios: nombre, documento y email' };
            }

            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';

            const response = await fetch(`${SUPABASE_URL}/rest/v1/candidate`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(transformedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al crear candidato' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;

        } catch (error) {
            console.error('Error al crear candidato:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /*
     * Actualiza un candidato existente
     */
    async updateCandidate(candidateId, candidateData) {
        try {
            const transformedData = this.transformFormDataToAPI(candidateData);
            
            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';
            
            const url = `${SUPABASE_URL}/rest/v1/candidate?id_candidato=eq.${candidateId}`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: headers,
                body: JSON.stringify(transformedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al actualizar candidato' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;
            
        } catch (error) {
            console.error('Error al actualizar candidato:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /*
     * Elimina un candidato
     */
    async deleteCandidate(candidateId) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?id_candidato=eq.${candidateId}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al eliminar candidato' };
            }

            return true;
        } catch (error) {
            console.error('Error al eliminar candidato:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /*
     * Transforma datos del Excel al formato del formulario que espera el sistema
     */
    transformExcelDataToForm(excelRow) {
        // Función helper para limpiar strings
        const cleanString = (value) => {
            if (value === null || value === undefined) return '';
            return String(value).trim();
        };

        // Función helper para procesar habilidades
        const processSkills = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            // Si es string, dividir por coma y limpiar espacios
            return String(value).split(',').map(s => s.trim()).filter(s => s);
        };

        return {
            nombreCompleto: cleanString(excelRow["Nombre Completo"]),
            numIdentificacion: cleanString(excelRow["Num Identificación"]),
            correo: cleanString(excelRow["Correo"]),
            fechaNacimiento: excelRow["Fecha Nacimiento"] || '',
            edad: cleanString(excelRow["Edad"]),
            genero: cleanString(excelRow["Genero"]),
            direccion: cleanString(excelRow["Direccion"]),
            nivelEducativo: cleanString(excelRow["Nivel Educativo"]),
            experienciaLaboral: cleanString(excelRow["Experiencia laboral"]),
            habilidadesDestacadas: processSkills(excelRow["Habilidades Destacadas"]),
            motivacionCargo: cleanString(excelRow["Motivacion por el Cargo"]),
            cargoAplicado: cleanString(excelRow["Cargo Aplicado"]),
            ubicacionGeografica: cleanString(excelRow["Ubicacion Geografica"]),
            pais: cleanString(excelRow["Pais"]) || 'Colombia'
        };
    },

    /*
     * Crea múltiples candidatos con mejor manejo de errores
     * createbulkCandidates: metodo responsable de la carga masiva de candidatos desde un array de objetos
     */
    async createBulkCandidates(candidatesArray) {
        try {
            console.log(`Iniciando carga masiva de ${candidatesArray.length} candidatos...`);

            const results = {
                success: 0,
                errors: [],
                duplicates: [],
                total: candidatesArray.length
            };

            for (let i = 0; i < candidatesArray.length; i++) {
                const rowNumber = i + 2; // +2 porque la fila 1 es el header y empezamos en 0
                
                try {
                    // 1. Transformar datos del Excel
                    const candidateData = this.transformExcelDataToForm(candidatesArray[i]);
                    
                    console.log(`Procesando fila ${rowNumber}:`, candidateData.nombreCompleto);

                    // 2. Validar datos
                    const validationErrors = this.validateCandidateData(candidateData);
                    if (validationErrors.length > 0) {
                        throw new Error(validationErrors.join(', '));
                    }

                    // 3. Verificar duplicados
                    const duplicate = await this.checkDuplicateCandidate(
                        candidateData.numIdentificacion,
                        candidateData.correo
                    );

                    if (duplicate.exists) {
                        results.duplicates.push({
                            row: rowNumber,
                            nombre: candidateData.nombreCompleto,
                            documento: candidateData.numIdentificacion,
                            error: duplicate.message
                        });
                        console.log(`⚠️ Fila ${rowNumber}: Duplicado - ${duplicate.message}`);
                        continue; // Saltar este registro
                    }

                    // 4. Crear candidato
                    await this.createCandidate(candidateData);
                    results.success++;
                    console.log(`✅ Fila ${rowNumber}: Candidato creado exitosamente`);

                } catch (error) {
                    results.errors.push({
                        row: rowNumber,
                        nombre: candidatesArray[i]["Nombre Completo"] || 'N/A',
                        documento: candidatesArray[i]["Num Identificación"] || 'N/A',
                        error: error.message || 'Error desconocido'
                    });
                    console.error(`❌ Fila ${rowNumber}: Error -`, error.message);
                }
            }

            console.log(`Carga masiva finalizada:`, results);

            return results;
        } catch (error) {
            console.error('Error crítico en carga masiva:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    // Mantener todos los demás métodos existentes...
    async getCandidatesByBirthDate(fecha) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?fecha_nacimiento=eq.${fecha}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por fecha' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por fecha:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByDocument(documento) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?documento=eq.${documento}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por documento' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por documento:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByEducationLevel(nivelEducativo) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?nivel_educativo=eq.${encodeURIComponent(nivelEducativo)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por nivel educativo' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por nivel educativo:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByGender(genero) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?genero=eq.${encodeURIComponent(genero)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por género' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por género:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByAge(edad) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?edad=eq.${edad}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por edad' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por edad:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByCountry(pais) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?pais=eq.${encodeURIComponent(pais)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por país' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por país:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByCity(ciudad) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?ciudad=eq.${encodeURIComponent(ciudad)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por ciudad' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por ciudad:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    async getCandidatesByExperience(experiencia) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/candidate?experiencia_laboral=eq.${encodeURIComponent(experiencia)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener candidatos por experiencia' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener candidatos por experiencia:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    }
};

export default candidateService;